// @flow
/* global window */

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { selectCartUUID, updateCart } from '@gandi/cart';
import { selectFromConfig } from '@gandi/redux-config';
import {
  fetchCountry,
  fetchCountryList,
  selectIsFailureCountry,
  selectIsFetchingCountry,
  selectIsLoadedCountry,
  selectCurrencies,
} from '@gandi/redux-places';
import { localeSelector, useTranslator } from '@gandi/translate';
import {
  Base,
  Button,
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  Switch,
} from '@gandi/ui';

import { useCreateHref, useQuery } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  type Option,
  relocalizeUrl,
} from '../../../modules/app/selectors/locale';
import type {
  PreFetchArgs,
  PreFetchReturn,
  ComponentWithPrefetch,
  FullState,
} from '../../../types';
import type { AnonymousPreferencesWidgetQuery as Query } from '../../../types/query';
import {
  selectCountry,
  selectCurrency,
  selectTaxesIncluded,
} from '../../modules/selectors';

import CountrySelect from './CountrySelect';
import CurrencySelect from './CurrencySelect';
import Fieldset from './Fieldset';
import LocaleSelect from './LocaleSelect';
import type { DialogState } from './types';

import styles from './AnonymousDialog.scss';

const selectEnableTaxesSwitch = selectFromConfig<boolean>(
  'flags.ENABLE_VAT_SWITCH',
  false,
);

type Props = {
  displayTaxesSwitch: boolean,
  dialogState: DialogState,
};

function AnonymousDialog({
  dialogState,
  displayTaxesSwitch,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const createHref = useCreateHref();
  const [searchParams] = useSearchParams();

  const cartId = useAppSelector(selectCartUUID);

  const {
    taxes,
    country: locationQueryCountry,
    currency: locationQueryCurrency,
  } = useQuery<Query>();

  // locale
  const defaultLocale = useAppSelector(localeSelector);
  const [locale, setLocale] = React.useState(defaultLocale);

  // $FlowFixMe[incompatible-call]
  const localeSelectOnChange = (option: Option) => setLocale(option.value);

  // country
  const defaultCountry = useAppSelector(state =>
    selectCountry(state, { locationQueryCountry }),
  );
  const [country, setCountry] = React.useState(defaultCountry);

  const isFetchingCountry = useAppSelector(state =>
    selectIsFetchingCountry(state, country),
  );
  const isFailureCountry = useAppSelector(state =>
    selectIsFailureCountry(state, country),
  );
  const isLoadedCountry = useAppSelector(state =>
    selectIsLoadedCountry(state, country),
  );

  const countrySelectOnChange = (option: Option) => {
    setCountry(option.value);
  };

  React.useEffect(() => {
    if (!isFetchingCountry && (isFailureCountry || !isLoadedCountry)) {
      dispatch(fetchCountry(country));
    }
  }, [dispatch, country, isFetchingCountry, isFailureCountry, isLoadedCountry]);

  // currency
  const defaultCurrency = useAppSelector(state =>
    selectCurrency(state, {
      locationQueryCurrency,
      country,
    }),
  );
  const [currency, setCurrency] = React.useState(defaultCurrency);

  const currencySelectOnChange = (option: Option) => setCurrency(option.value);

  const currencies = useAppSelector(state => selectCurrencies(state, country));
  React.useEffect(() => {
    if (!isFetchingCountry && isLoadedCountry) {
      const isCurrencyAvailable = !!currencies.find(c => c === currency);
      if (!isCurrencyAvailable) {
        setCurrency(currencies[0]);
      }
    }
  }, [isFetchingCountry, isLoadedCountry, currencies, currency]);

  // taxes
  const enableTaxesSwitch = useAppSelector(selectEnableTaxesSwitch);

  const taxesIncludedDefault = useAppSelector(state =>
    selectTaxesIncluded(state, taxes),
  );

  const [taxesIncluded, setTaxesIncluded] =
    React.useState(taxesIncludedDefault);

  const taxesSwitchOnChange = (evt: SyntheticInputEvent<>) =>
    setTaxesIncluded(evt.target.checked);

  // submitting
  const [submitting, setSubmitting] = React.useState(false);
  const handleSubmit: (evt: SyntheticEvent<>) => Promise<void> = async (
    evt: SyntheticEvent<>,
  ) => {
    evt.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);

    if (cartId) {
      try {
        await dispatch(
          // updateCart should accept any cart props instead of only products one
          updateCart(cartId, {
            currency,
            country,
          }),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        // pass
      }
    }

    searchParams.set('country', country);
    searchParams.set('currency', currency);
    searchParams.set('taxes', taxesIncluded ? 'yes' : 'no');

    const newLocation = {
      search: searchParams.toString(),
    };

    let url = createHref(newLocation);

    // apply the locale like it is done with the bottom locale select
    if (defaultLocale !== locale) {
      url = relocalizeUrl(url, defaultLocale, locale);
    }

    window.location.href = url;
  };

  return (
    <Dialog {...dialogState} scroll="body">
      <DialogHeader>{__('Preferences')}</DialogHeader>
      <DialogContent>
        <Fieldset inputName="localeSelect" label={__('Change language')}>
          <LocaleSelect
            onChange={localeSelectOnChange}
            disabled={submitting}
            value={locale}
            name="localeSelect"
          />
        </Fieldset>
        <Fieldset inputName="countrySelect" label={__('Prices for')}>
          <CountrySelect
            onChange={countrySelectOnChange}
            disabled={submitting}
            value={country}
            name="countrySelect"
          />
        </Fieldset>
        <Base className={styles.columns}>
          <Fieldset column inputName="currencySelect" label={__('Currency')}>
            <CurrencySelect
              onChange={currencySelectOnChange}
              country={country}
              disabled={submitting}
              loading={isFetchingCountry}
              value={currency}
              name="currencySelect"
            />
          </Fieldset>
          {enableTaxesSwitch && (
            <Fieldset column inputName="taxes" label={__('Taxes')}>
              <Base flex flexAlignItemsCenter className={styles.switch}>
                <Switch
                  name="taxes"
                  onChange={taxesSwitchOnChange}
                  checked={taxesIncluded}
                  disabled={submitting || !displayTaxesSwitch}
                >
                  {__('with taxes')}
                </Switch>
              </Base>
            </Fieldset>
          )}
        </Base>
      </DialogContent>
      <DialogFooter>
        <Button type="button" kind="outline" onClick={dialogState.close} small>
          {__('Cancel')}
        </Button>
        <Button type="button" onClick={handleSubmit} small>
          {__('Save')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

AnonymousDialog.preFetch = function preFetch(
  args: PreFetchArgs<{}, void, Query, FullState>,
): PreFetchReturn<> {
  const { dispatch, getState } = args;
  const country = selectCountry(getState(), {
    locationQueryCountry: args.query.country,
  });

  const promises = [dispatch(fetchCountryList())];

  if (typeof country === 'string') {
    promises.push(dispatch(fetchCountry(country)));
  }

  return Promise.allSettled(promises);
};

export default (AnonymousDialog: ComponentWithPrefetch<
  Props,
  typeof AnonymousDialog.preFetch,
>);
